import { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardService, normalizeDashboardOverviewResponse } from '../services/dashboardService';
import { apiService, ApiError } from '../services/apiService';
import {
  AlertConfiguration,
  DashboardOverviewResponse,
  DashboardConfiguration,
  DashboardWidget,
  Subscription,
} from '../types/dashboard';
import { useAuth } from '../contexts/AuthContext';
import { buildAlertSlotMonitorFromConfigs } from '../utils/alertSlotMonitor';
import { enrichPerformanceMetricsFromHistory } from '../utils/enrichPerformanceMetrics';
import {
  buildIncomingWebhookNotices,
  mergeNoticesIntoSlotRows,
} from '../utils/incomingWebhookNotices';

export function useDashboard(tenantId: string, userId: number) {
  const { token } = useAuth();
  const [data, setData] = useState<DashboardOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track whether the first load has completed so background refreshes don't
  // flip the page back to the loading skeleton (which remounts content and
  // scrolls the user back to the top).
  const hasLoadedRef = useRef(false);

  const fetchDashboardData = useCallback(async () => {
    const isInitialLoad = !hasLoadedRef.current;
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      
      // Load subscription data from API
      let subscriptionData: Subscription | null = null;
      try {
        const subResponse = await apiService.getSubscription(token || undefined);
        if (subResponse.success && subResponse.data) {
          // Map tier to plan, ensuring it's one of the valid plan types
          const tier = subResponse.data.tier || 'trial';
          const legacyPlan: Record<string, Subscription['plan']> = {
            basic: 'starter',
            standard: 'pro',
            enterprise: 'ultimate',
            desktop: 'pro',
          };
          const plan: Subscription['plan'] =
            tier === 'trial'
              ? 'trial'
              : (legacyPlan[tier] ||
                  (['starter', 'pro', 'elite', 'ultimate'].includes(tier)
                    ? (tier as Subscription['plan'])
                    : 'trial'));
          
          // Map status, ensuring it's one of the valid status types
          const statusStr = subResponse.data.status || 'active';
          const status: 'active' | 'expired' | 'suspended' = 
            statusStr === 'active' || statusStr === 'trial' ? 'active' :
            statusStr === 'expired' ? 'expired' :
            statusStr === 'suspended' || statusStr === 'cancelled' ? 'suspended' : 'active';
          
          // Use subscription_end_date if available, otherwise trial_end_date, otherwise default to 30 days
          const endDate = subResponse.data.subscription_end_date || 
                         subResponse.data.trial_end_date ||
                         new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
          
          subscriptionData = {
            plan,
            status,
            webhookLimit: subResponse.data.webhook_limit || 999,
            webhookUsage: 0, // TODO: Calculate from actual webhooks
            storageLimit: 0.1,
            storageUsage: 0,
            expiresAt: endDate,
            autoRenew: false
          };
        }
      } catch (subError) {
        console.log('Failed to load subscription data:', subError);
      }
      
      // Fetch dashboard overview from API (body is { status, overview: {...} })
      const [response, configsRes, historyRes] = await Promise.all([
        dashboardService.getDashboardOverview(tenantId, userId, token || undefined),
        apiService.getAlertConfigurations(token || undefined).catch(() => ({ success: false as const, data: [] as const })),
        apiService.getAlertHistory(1, 25, token || undefined).catch(() => ({ success: false as const, data: [] as const })),
      ]);

      const normalized = normalizeDashboardOverviewResponse(response.data, subscriptionData);
      if (normalized) {
        // User-assigned alert names (from each config's description), keyed by config id,
        // so we can label slot-monitor rows and recent-activity entries by name.
        const nameById = new Map<string, string>();
        if (configsRes.success && Array.isArray(configsRes.data)) {
          const list = configsRes.data as AlertConfiguration[];
          list.forEach((c) => {
            const nm = (c.description ?? '').trim();
            if (nm) nameById.set(String(c.id), nm);
          });
          const total = list.length;
          const active = list.filter((c) => c.is_active).length;
          const error = list.filter((c) => c.last_test_status === 'failed').length;
          normalized.webhookSummary = {
            total,
            active,
            inactive: Math.max(0, total - active),
            error,
          };
          const notices = await buildIncomingWebhookNotices(list, token || undefined);
          normalized.alertNotices = notices;

          if (!normalized.alertSlotMonitor?.length) {
            normalized.alertSlotMonitor = await buildAlertSlotMonitorFromConfigs(
              list,
              token || undefined
            );
          }
          if (normalized.alertSlotMonitor?.length && notices.length) {
            normalized.alertSlotMonitor = mergeNoticesIntoSlotRows(
              normalized.alertSlotMonitor,
              notices
            );
          }
        } else {
          normalized.alertNotices = normalized.alertNotices ?? [];
        }
        if (historyRes.success && Array.isArray(historyRes.data)) {
          normalized.recentAlerts = historyRes.data;
          normalized.performanceMetrics = enrichPerformanceMetricsFromHistory(
            normalized.performanceMetrics,
            historyRes.data
          );
        }
        // Attach user-assigned names so the Live trades panel and Recent activity
        // list show the alert's name rather than just the ticker.
        if (nameById.size) {
          if (normalized.alertSlotMonitor?.length) {
            normalized.alertSlotMonitor = normalized.alertSlotMonitor.map((r) => ({
              ...r,
              alertName: nameById.get(String(r.alertId)) ?? r.alertName,
            }));
          }
          if (normalized.recentAlerts?.length) {
            normalized.recentAlerts = normalized.recentAlerts.map((a) => ({
              ...a,
              alertName:
                a.alertConfigId != null
                  ? nameById.get(String(a.alertConfigId)) ?? a.alertName
                  : a.alertName,
            }));
          }
        }
        setData(normalized);
      } else {
        const errorMessage = response.error || 'Failed to load dashboard data';
        setError(errorMessage);
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to fetch dashboard data';
      setError(errorMessage);
      console.error('Dashboard fetch error:', err);
    } finally {
      hasLoadedRef.current = true;
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  }, [tenantId, userId, token]);

  useEffect(() => {
    if (tenantId && userId) {
      fetchDashboardData();
    }
  }, [tenantId, userId, fetchDashboardData]);

  return {
    data,
    loading,
    refreshing,
    error,
    refetch: fetchDashboardData
  };
}

export function useDashboardConfig(tenantId: string, userId: number) {
  const { token } = useAuth();
  const [config, setConfig] = useState<DashboardConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try real API call first
      try {
        const response = await dashboardService.getDashboardConfig(tenantId, userId, token || undefined);
        if (response.success && response.data) {
          setConfig(response.data);
          return;
        }
      } catch (apiError) {
        console.log('API dashboard config call failed, using mock data:', apiError);
      }
      
      // Fallback to mock data for development
      if (process.env.NODE_ENV === 'development') {
        console.log('Using mock dashboard config for development');
        const mockConfig: DashboardConfiguration = {
          id: 1,
          tenantId: parseInt(tenantId),
          userId: userId,
          layoutConfig: {
            layout: 'grid',
            columns: 12,
            rowHeight: 60
          },
          widgetConfig: {
            system_status: { show_uptime: true, show_errors: true },
            alerts: { max_items: 10, show_confidence: true }
          },
          themePreferences: {
            theme: 'light',
            primaryColor: '#1976d2',
            secondaryColor: '#dc004e'
          },
          notificationPreferences: {
            email: true,
            browser: true,
            sound: false
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setConfig(mockConfig);
      } else {
        const response = await dashboardService.getDashboardConfig(tenantId, userId, token || undefined);
        setConfig(response.data);
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to fetch dashboard configuration';
      setError(errorMessage);
      console.error('Dashboard config fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, userId, token]);

  const updateConfig = useCallback(async (newConfig: Partial<DashboardConfiguration>) => {
    try {
      setError(null);
      const response = await dashboardService.updateDashboardConfiguration(tenantId, userId, newConfig);
      setConfig(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to update dashboard configuration';
      setError(errorMessage);
      console.error('Dashboard config update error:', err);
      throw err;
    }
  }, [tenantId, userId]);

  useEffect(() => {
    if (tenantId && userId) {
      fetchConfig();
    }
  }, [fetchConfig, tenantId, userId]);

  return {
    config,
    loading,
    error,
    updateConfig,
    refetch: fetchConfig
  };
}

export function useDashboardWidgets(tenantId: string, userId: number) {
  const { token } = useAuth();
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWidgets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try real API call first
      try {
        const response = await dashboardService.getDashboardWidgets(tenantId, userId, token || undefined);
        if (response.success && response.data) {
          setWidgets(response.data);
          return;
        }
      } catch (apiError) {
        console.log('API dashboard widgets call failed, using mock data:', apiError);
      }
      
      // Fallback to mock data for development
      if (process.env.NODE_ENV === 'development') {
        console.log('Using mock dashboard widgets for development');
        const mockWidgets: DashboardWidget[] = [
          {
            id: 1,
            tenantId: parseInt(tenantId),
            userId: userId,
            widgetType: 'system_status',
            widgetPosition: { x: 0, y: 0, w: 4, h: 2 },
            widgetConfig: { show_uptime: true, show_errors: true },
            isVisible: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          {
            id: 2,
            tenantId: parseInt(tenantId),
            userId: userId,
            widgetType: 'alerts',
            widgetPosition: { x: 4, y: 0, w: 4, h: 2 },
            widgetConfig: { max_items: 10, show_confidence: true },
            isVisible: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        ];
        setWidgets(mockWidgets);
      } else {
        const response = await dashboardService.getDashboardWidgets(tenantId, userId, token || undefined);
        setWidgets(response.data);
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to fetch dashboard widgets';
      setError(errorMessage);
      console.error('Dashboard widgets fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, userId, token]);

  const createWidget = useCallback(async (widget: Omit<DashboardWidget, 'id' | 'tenantId' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    try {
      setError(null);
      const response = await dashboardService.createDashboardWidget(tenantId, userId, widget);
      setWidgets(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to create dashboard widget';
      setError(errorMessage);
      console.error('Dashboard widget create error:', err);
      throw err;
    }
  }, [tenantId, userId]);

  const updateWidget = useCallback(async (widgetId: number, widget: Partial<DashboardWidget>) => {
    try {
      setError(null);
      const response = await dashboardService.updateDashboardWidget(tenantId, userId, widgetId, widget);
      setWidgets(prev => prev.map(w => w.id === widgetId ? response.data : w));
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to update dashboard widget';
      setError(errorMessage);
      console.error('Dashboard widget update error:', err);
      throw err;
    }
  }, [tenantId, userId]);

  const deleteWidget = useCallback(async (widgetId: number) => {
    try {
      setError(null);
      await dashboardService.deleteDashboardWidget(tenantId, userId, widgetId);
      setWidgets(prev => prev.filter(w => w.id !== widgetId));
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to delete dashboard widget';
      setError(errorMessage);
      console.error('Dashboard widget delete error:', err);
      throw err;
    }
  }, [tenantId, userId]);

  useEffect(() => {
    if (tenantId && userId) {
      fetchWidgets();
    }
  }, [tenantId, userId, fetchWidgets]);

  return {
    widgets,
    loading,
    error,
    createWidget,
    updateWidget,
    deleteWidget,
    refetch: fetchWidgets
  };
}

export function useDashboardApiOperation<T extends any[], R>(
  operation: (service: any, ...args: T) => Promise<R>
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async (...args: T): Promise<R | null> => {
    try {
      setLoading(true);
      setError(null);
      const result = await operation(dashboardService, ...args);
      return result;
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Operation failed';
      setError(errorMessage);
      console.error('Dashboard API operation error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [operation]);

  return {
    execute,
    loading,
    error
  };
}